import { useNavigate } from 'react-router-dom'
import { faSun } from '@fortawesome/pro-duotone-svg-icons'

import Icon from '../icon/Icon'
import { useDailyPepTalk } from '../../_hooks/use-daily-pep-talk'
import {
  Card,
  Eyebrow,
  Glyph,
  Greeting,
  Body,
  NudgeRow,
  NudgeText,
  NudgeButton,
  SkeletonLine,
  QuietText,
} from './styled'
import { DailyPepTalkProps } from './types'

const DEFAULT_CTA_PATH = '/saved-listings'

/**
 * Dashboard widget that renders a once-per-day LLM pep-talk for the
 * renter. Quietly returns null when offline so the dashboard never
 * shows a broken card.
 *
 * @param {DailyPepTalkProps} props
 * @returns {JSX.Element | null}
 */
const DailyPepTalk = ({
  ctaPath = DEFAULT_CTA_PATH,
}: DailyPepTalkProps): JSX.Element | null => {
  const { state, result } = useDailyPepTalk()
  const navigate = useNavigate()

  if (state === 'idle' || state === 'offline') {
    return null
  }

  const handleNudgeClick = (): void => {
    navigate(ctaPath)
  }

  if (state === 'loading') {
    return (
      <Card>
        <Eyebrow>
          <Glyph aria-hidden="true">
            <Icon icon={faSun} />
          </Glyph>
          Today
        </Eyebrow>
        <SkeletonLine style={{ width: '40%' }} />
        <SkeletonLine style={{ width: '90%' }} />
        <SkeletonLine style={{ width: '70%' }} />
      </Card>
    )
  }

  if (state === 'error') {
    return null
  }

  if (result === null) {
    return null
  }

  return (
    <Card>
      <Eyebrow>
        <Glyph aria-hidden="true">
          <Icon icon={faSun} />
        </Glyph>
        Today
      </Eyebrow>
      <Greeting>{result.greeting}</Greeting>
      <Body>{result.body}</Body>
      <NudgeRow>
        <NudgeText>{result.microNudge}</NudgeText>
        <NudgeButton type="button" onClick={handleNudgeClick}>
          Let&apos;s go
        </NudgeButton>
      </NudgeRow>
      <QuietText>Refreshes once a day.</QuietText>
    </Card>
  )
}

export default DailyPepTalk
